<template>
  <div class="order-recovery-modal" @keydown.esc="secondaryBtnAction">
    <div class="order-recovery-modal__container">
      <p class="small-headline">
        {{ $t('OrderRecoveryModal.label.subhead') }}
      </p>

      <h1 class="extra-large-headline">
        {{ $t('OrderRecoveryModal.label.headline') }}
      </h1>

      <p class="bodycopy">
        {{ $t('OrderRecoveryModal.label.body') }}
      </p>

      <div class="order-recovery-modal__price" v-if="newPrice">
        <div class="order-recovery-modal__price__item">
          <div class="order-recovery-modal__price__text">
            <p>{{ $t('OrderRecoveryModal.label.spend') }} {{ newPrice.fiatType }}</p>
            <p><strong>{{ toCurrencyFormat(newPrice.fiatAmount, newPrice.fiatType) }}</strong></p>
          </div>
          <div class="order-recovery-modal__price__icon">
            <span :class="`currency-flag currency-flag-${newPrice.fiatType.toLowerCase()}`" />
          </div>
        </div>

        <div class="order-recovery-modal__price__item">
          <div class="order-recovery-modal__price__icon">
            <CryptoIcon class="crypto-icon" :symbol="`${newPrice.coinType.toLowerCase()}`" :size="33" />
          </div>
          <div class="order-recovery-modal__price__text">
            <p>{{ $t('OrderRecoveryModal.label.receive') }} {{ newPrice.coinType }}</p>
            <p><strong>{{ newPrice.coinAmount }}</strong></p>
          </div>
        </div>
      </div>

      <ButtonBase @click="primaryBtnAction" v-if="orderId">
        {{ $t('ButtonBase.label.confirmRecovery') }}
      </ButtonBase>

      <ButtonBase @click="secondaryBtnAction" outlined v-if="orderId">
        {{ $t('ButtonBase.label.declineRecovery') }}
      </ButtonBase>
    </div>
  </div>
</template>
<script setup>
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import gsap from 'gsap';

import { useIdentityQueueStore } from '@/stores/identityQueueStore';

import ButtonBase from '@/components/ui/buttons/ButtonBase.vue';
import useApiHelpers from '@/composables/api/helpers';
import useAuthToken from '@/composables/api/useAuthToken';
import useModalActions from '@/composables/store/modal/useModalActions';
import CryptoIcon from '@/components/icons/CryptoIcon.vue';
import useErrorHandling from '@/composables/api/useErrorHandling';
import { toCurrencyFormat } from '@/composables/helpers/useStringHelpers';

const { toggleOrderRecoveryModal } = useModalActions();
const { waitUntilTokenRetrieved } = useAuthToken();
const { getRequest, postRequest } = useApiHelpers();
const { handleErrorInSpaFlow } = useErrorHandling();
const identityQueueStore = useIdentityQueueStore();

const router = useRouter();
const route = useRoute();

const newPrice = ref(null);
const sessionData = ref(null);
const orderId = ref(null);

const handleOrderRecovery = async () => {
  const orderResponse = await postRequest('/orders/buy', { ...sessionData.value }, false, false);
  const needsAuthentication = orderResponse.data.rel.route === 'authentication';
  const needsContactDetails = orderResponse.data.rel.route === 'account';

  if (needsContactDetails) {
    router.push('/verification');
    return;
  }

  if (needsAuthentication) {
    router.push('/verification/code');
    return;
  }

  const nextRoute = await identityQueueStore.fetchNextIdentityRoute(route);
  router.push(nextRoute);
};

const handleOrderRecoveredEvent = async () => {
  postRequest('/order/event/', {
      order_id: orderId.value,
      event: 'order_recovered',
  }, false, false);
};

const primaryBtnAction = async () => {
  if (orderId.value === null) {
    return;
  }

  try {
    await handleOrderRecovery();
    handleOrderRecoveredEvent();
  } catch (err) {
    handleErrorInSpaFlow(40441, 'Order not found. Please try again.');
  } finally {
    toggleOrderRecoveryModal(false);
  }
};

const secondaryBtnAction = () => {
  postRequest('/order/event/', {
      order_id: orderId.value,
      event: 'order_requote_rejected',
  }, false, false);
  toggleOrderRecoveryModal(false);
  router.push('/');
};

const getRehydratedSession = async () => {
  try {
    const newSession = await getRequest(`/recover/${orderId.value || ''}`);
    return newSession.data;
  } catch (err) {
    if (err.response.data.code === 42204) {
      handleErrorInSpaFlow(42204, err.response.data.message);
    } else {
      handleErrorInSpaFlow(40441, 'Order not found. Please try again.');
    }
    toggleOrderRecoveryModal(false);

    return null;
  }
};

const fetchPricing = async () => {
  sessionData.value = await getRehydratedSession();

  if (sessionData.value === null) {
    throw new Error('OrderRecoveryModal: No session data found');
  }

  const {
    fiatAmount,
    fiatType,
    coinAmount,
    coinType,
    lockFiat,
    blockchain,
    payment,
    orderType,
    subscriptionId = null,
  } = sessionData.value;

  await postRequest('/pricing/calculate', {
    coinAmount,
    fiatAmount,
    fiatType,
    payment,
    coinType,
    lockFiat,
    blockchain,
    subscriptionId,
    orderType: orderType === 'CRYPTO-BUY' ? 'BUY' : 'SELL',
  }, (response) => {
    newPrice.value = response.data;
  });
};

const animateModal = () => {
  const successTl = gsap.timeline();
  successTl.fromTo(
    '.order-recovery-modal',
    {
      duration: 0.75,
      y: 30,
      opacity: 0,
      delay: 0.5,
    },
    {
      y: 0,
      opacity: 1,
      delay: 0.5,
      ease: 'inOut.power3',
    },
  );
};

onMounted(async () => {
  await waitUntilTokenRetrieved();
  fetchPricing();
  animateModal();
});

watch(() => route.params.orderId, async () => {
  if (route.params.orderId === undefined) {
    return;
  }

  orderId.value = route.params.orderId;
});

</script>
<style lang="scss" scoped>
.order-recovery-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  background: var(--modal-content-background);
  border-radius: $border-radius $border-radius 0 0;

  &__price {
    background: var(--input-place-holder-text-color);
    border-radius: 1.6rem;
    padding: 1rem 2rem;
    display: flex;
    justify-content: center;
    gap: 3rem;
    margin-bottom: 1.8rem;

    &__item {
      display: flex;
      gap: 0.5rem;

      span {
        width: 33px;
        height: 33px;
        background-position: cover;
        background-position: center;
        border-radius: 50%;
      }
    }

    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      p {
        font-size: 1rem;
        line-height: 1.25;
        strong {
          font-size: 1.4rem;
        }
      }

      &:nth-child(1){
        text-align: right;
      }
    }

  }

  .crypto-icon {
    width: 33px;
    height: 33px;

    & > img {
      width: 100%;
    }
  }

  @include breakpoint($min-m) {
    border-radius: $border-radius;
  }

  &__container {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    padding: 1.5rem 3rem;

    @include breakpoint($min-m) {
      padding: 3rem;
      max-width: 468px;
    }

    .bodycopy {
      margin-bottom: 1.8rem;
    }

    .button {
      margin-bottom: 1rem;
    }

    .small-headline {
      color: var(--primary);
      margin-bottom: 1.8rem;
    }
  }
}
</style>
